import { createGlobalStyle, css } from 'styled-components'
import { rem, mix } from 'polished'

import { getiOSVersion } from 'utils/userAgent'
import { breakpoint } from 'utils'
import { selectStyles } from 'components/Select'

import {
  regular,
  regularItalic,
  medium,
  semibold,
  bold,
  mono,
} from './fonts/base64'

interface GlobalStyleProps {
  userAgent: string
}

const marfaFonts = css`
  @font-face {
    font-family: 'Marfa';
    font-style: normal;
    font-weight: normal;
    font-display: optional;
    src: url(${regular}) format('woff2');
  }

  @font-face {
    font-family: 'Marfa';
    font-weight: normal;
    font-style: italic;
    font-display: optional;
    src: url(${regularItalic}) format('woff2');
  }

  @font-face {
    font-family: 'Marfa';
    font-weight: 500;
    font-display: optional;
    src: url(${medium}) format('woff2');
  }

  @font-face {
    font-family: 'Marfa';
    font-weight: 600;
    font-display: optional;
    src: url(${semibold}) format('woff2');
  }

  @font-face {
    font-family: 'Marfa';
    font-weight: 700;
    font-display: optional;
    src: url(${bold}) format('woff2');
  }

  @font-face {
    font-family: 'Marfa Mono';
    font-style: normal;
    font-weight: normal;
    font-display: optional;
    src: url(${mono}) format('woff2');
  }
`

export const linkStyles = css(
  ({ theme }) => css`
    transition: color ${theme.transitions.duration[1]};
    text-decoration: none;
    white-space: nowrap;
    color: ${theme.colors.primaryTint[100]};

    &:hover {
      color: ${mix(0.2, theme.colors.white, theme.colors.primaryTint[100])};
    }

    &:active {
      color: ${mix(0.2, theme.colors.black, theme.colors.primaryTint[100])};
    }
  `
)

const GlobalStyle = createGlobalStyle<GlobalStyleProps>(
  ({ theme, userAgent }) => css`
    /* stylelint-disable-next-line no-descending-specificity */
    :root {
      /* Avoids a warning from Reach UI to import the listbox styles */
      --reach-listbox: 1;
    }

    ${marfaFonts};

    html {
      height: 100%;
      /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
      height: -webkit-fill-available;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      overflow-x: hidden;
    }

    *,
    *:before,
    *:after {
      box-sizing: border-box;
    }

    * {
      ${() => {
        const iOS = getiOSVersion(userAgent)

        if (!iOS || iOS >= 13) {
          return undefined
        }

        return { '-webkit-overflow-scrolling': 'touch' }
      }}
    }

    /* Only browsers supporting :focus-visible will remove the outline for :focus */
    :focus:not(:focus-visible) {
      outline: none;
    }

    body {
      display: flex;
      flex-direction: column;
      min-height: 100%;
      height: 100%;
      font-family: ${theme.fonts.body};
      color: ${theme.colors.text.dark.base};
      font-size: ${theme.bodyFontSize};
      line-height: 1.5;
      background-color: ${theme.colors.background.secondary[1]};

      /* Normalize font rendering, specially in Firefox where Marfa renders too bold */
      -moz-osx-font-smoothing: grayscale;
      -webkit-font-smoothing: antialiased;
      font-weight: 400;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    .display-1,
    .h1,
    .h2,
    .h3,
    .h4,
    .h5,
    .h6,
    .caption {
      font-weight: ${theme.fontWeights.medium};
      font-style: normal;
    }

    .h5,
    .h6 {
      letter-spacing: initial;
    }

    .display-1 {
      font-size: ${rem(54)};
    }

    h1,
    .h1 {
      font-size: ${rem(44)};
      line-height: 1.1; /* stylelint-disable-line scales/line-height */
      margin-top: 48px;
      margin-bottom: 32px;
      font-weight: ${theme.fontWeights.bold};
    }

    h2,
    .h2 {
      font-size: ${rem(32)};
      line-height: 1.25;
      margin-top: 48px;
      margin-bottom: 24px;
    }

    h3,
    .h3 {
      font-size: ${rem(27)};
      line-height: 1.25;
      margin-top: 32px;
      margin-bottom: 16px;
    }

    h4,
    .h4 {
      font-size: ${rem(22)};
      line-height: 1.3; /* stylelint-disable-line scales/line-height */
      margin-top: 32px;
      margin-bottom: 16px;
    }

    h5,
    .h5 {
      font-size: ${rem(16)};
      line-height: 1.5;
      margin-top: 32px;
      margin-bottom: 8px;
    }

    h6,
    .h6,
    .caption {
      font-size: ${rem(12)};
      line-height: 1.75;
      letter-spacing: 0.32px;
      text-transform: uppercase;
      margin-top: 24px;
      margin-bottom: 8px;
    }

    p + p {
      margin-top: 16px;
    }

    a,
    .link {
      ${linkStyles};
    }

    p a {
      font-weight: ${theme.fontWeights.medium};
    }

    hr {
      height: 1px;
      background-color: ${theme.colors.border.dark.base};
      border: none;
      margin: 24px 0;
    }

    small {
      font-size: 0.8125rem;
      line-height: 1.25;
    }

    .t-lead {
      font-size: ${rem(20)};
      line-height: 1;
    }

    .t-small {
      color: rgba(#000, 0.65);
      font-size: ${rem(13)};
      line-height: 1.25;
    }

    .t-small.t-hint {
      color: rgba(#000, 0.4);
    }

    .dark-primary {
      color: rgba(0, 0, 0, 0.85);
    }

    .dark-secondary {
      color: rgba(0, 0, 0, 0.65);
    }

    .dark-tertiary {
      color: rgba(0, 0, 0, 0.4);
    }

    .light-primary {
      color: rgba(255, 255, 255, 0.85);
    }

    .light-secondary {
      color: rgba(255, 255, 255, 0.65);
    }

    .light-tertiary {
      color: rgba(255, 255, 255, 0.4);
    }

    #HW_frame_cont {
      max-width: 100%;
      margin-top: 6px; /* stylelint-disable-line scales/space */
      box-shadow: ${theme.shadows.boxShadow.elevated};
      border: 1px solid ${theme.colors.border.dark.base};
      background-clip: padding-box;
      box-sizing: content-box;
      overflow: visible;

      /* Popover arrow */
      &:before,
      &:after {
        content: '';
        position: absolute;
        z-index: -1;
      }

      &:before {
        top: -10px;
        right: 66px;

        ${breakpoint('xxs')`
          right: 56px;
        `}

        ${breakpoint('xs')`
          right: 16px;
        `}

        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-bottom: 9px solid ${theme.colors.border.dark.low};
      }

      &:after {
        top: -8px;
        right: 67px;

        ${breakpoint('xxs')`
          right: 57px;
        `}

        ${breakpoint('xs')`
          right: 17px;
        `}

        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 8px solid ${theme.colors.white};
      }

      /* stylelint-disable */

      /* Hack to override Headway's way to position the popover (hence the importants) on small screens, zero margin */
      left: 0 !important;

      /* 342px is the width of the popover itself 10px margin for still small screens */
      ${breakpoint('xxs')`
        left: calc(100% - 342px - 10px) !important;
    `}

      /* and 50px margin right for larger screens and beyond */
      ${breakpoint('xs')`
        left: calc(100% - 342px - 50px) !important;
      `}

    /* stylelint-enable */
    }

    /* stylelint-disable */
    #HW_badge_cont {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
        Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji',
        'Segoe UI Symbol', sans-serif;
      position: absolute !important;
      top: 0;
      right: 0;
      width: 100% !important;
      height: 100% !important;
    }

    #HW_badge {
      display: none !important;
      position: absolute;
      top: 8px !important;
      right: 8px !important;
      left: auto !important;
      bottom: auto !important;
    }

    /*
    This class basically hides a element to serve as a
    screen-reader element.

    source: https://www.scottohara.me/blog/2017/04/14/inclusively-hidden.html
    */
    .sr-only:not(:focus):not(:active) {
      clip: rect(0 0 0 0);
      clip-path: inset(100%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
    /* stylelint-enable */

    /**
    * Styles for <Select />
    */
    ${selectStyles}
  `
)

export { GlobalStyle }
